/**
 * API layer for workflow feature.
 *
 * Handles HTTP communication for pre-review, team review,
 * change requests, deprecation, and duplicate detection.
 */

import { apiClient } from '@/infrastructure/api/client';
import type { Translation } from '../translations/types';
import type {
  PreReviewQueuePage,
  ReviewQueuePage,
  CorrectionsQueuePage,
  ChangeRequestQueuePage,
  ChangeRequestTranslation,
  WorkflowQueueFilters,
  ApprovePreReviewPayload,
  RejectPreReviewPayload,
  ApproveReviewPayload,
  RejectReviewPayload,
  RequestChangePayload,
  RejectChangeRequestPayload,
  CreateChangeVersionPayload,
  DeprecateTranslationPayload,
  ResubmitCorrectionPayload,
  BulkSubmitPayload,
  BulkApprovePayload,
  BulkRejectPayload,
  BulkDeprecatePayload,
  BulkAssignTagsPayload,
  BulkOperationResult,
  DuplicateAnalysis,
  DuplicateDetectionFilters,
  SearchSimilarByValueResult,
  SearchSimilarByValueFilters,
} from './types';

const BASE_PATH = '/v1.0/i18n/translations';
const TOOLS_PATH = '/v1.0/i18n/tools';

// ---------- Queue Queries ----------

function buildQueueParams(
  filters: WorkflowQueueFilters
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  if (filters.page !== undefined) params.page = filters.page;
  if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;
  if (filters.sortBy) params.sortBy = filters.sortBy;
  if (filters.sortOrder) params.sortOrder = filters.sortOrder;
  // Note: myPendingOnly is handled client-side (not a backend-supported filter)
  return params;
}

export async function fetchPreReviewQueue(
  filters: WorkflowQueueFilters = {}
): Promise<PreReviewQueuePage> {
  const params = {
    ...buildQueueParams(filters),
    state: 'Pending Pre-Review',
    sortBy: filters.sortBy ?? 'lastUpdateDatetime',
    sortOrder: filters.sortOrder ?? 'DESC',
  };

  const response = await apiClient.get<{
    data: Translation[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>(BASE_PATH, params);

  return {
    items: response.data.data,
    total: response.data.total,
    page: response.data.page,
    pageSize: response.data.limit,
    totalPages: response.data.totalPages,
  };
}

export async function fetchReviewQueue(
  filters: WorkflowQueueFilters = {}
): Promise<ReviewQueuePage> {
  const params = {
    ...buildQueueParams(filters),
    state: 'In Review',
    sortBy: filters.sortBy ?? 'lastUpdateDatetime',
    sortOrder: filters.sortOrder ?? 'DESC',
  };

  const response = await apiClient.get<{
    data: Translation[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>(BASE_PATH, params);

  return {
    items: response.data.data,
    total: response.data.total,
    page: response.data.page,
    pageSize: response.data.limit,
    totalPages: response.data.totalPages,
  };
}

export async function fetchCorrectionsQueue(
  filters: WorkflowQueueFilters = {}
): Promise<CorrectionsQueuePage> {
  const params = {
    ...buildQueueParams(filters),
    state: 'Rejected (Pre-Review),Rejected (Review)',
    sortBy: filters.sortBy ?? 'lastUpdateDatetime',
    sortOrder: filters.sortOrder ?? 'DESC',
  };

  const response = await apiClient.get<{
    data: Translation[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>(BASE_PATH, params);

  return {
    items: response.data.data,
    total: response.data.total,
    page: response.data.page,
    pageSize: response.data.limit,
    totalPages: response.data.totalPages,
  };
}

export async function fetchChangeRequestQueue(
  filters: WorkflowQueueFilters = {}
): Promise<ChangeRequestQueuePage> {
  const params = {
    ...buildQueueParams(filters),
    state: 'Released',
    changeRequestState: 'Requested,Ready for Review',
    sortBy: filters.sortBy ?? 'lastUpdateDatetime',
    sortOrder: filters.sortOrder ?? 'DESC',
  };

  const response = await apiClient.get<{
    data: ChangeRequestTranslation[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>(BASE_PATH, params);

  return {
    items: response.data.data,
    total: response.data.total,
    page: response.data.page,
    pageSize: response.data.limit,
    totalPages: response.data.totalPages,
  };
}

// ---------- Workflow Actions ----------

export async function submitForReview(uuid: string): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/submit`,
    { uuid }
  );
  return response.data;
}

export async function approvePreReview(
  uuid: string,
  payload?: ApprovePreReviewPayload
): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/pre-review/approve`,
    { uuid, ...payload }
  );
  return response.data;
}

export async function rejectPreReview(
  uuid: string,
  payload: RejectPreReviewPayload
): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/pre-review/reject`,
    { uuid, ...payload }
  );
  return response.data;
}

export async function approveReview(
  uuid: string,
  payload?: ApproveReviewPayload
): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/review/approve`,
    { uuid, ...payload }
  );
  return response.data;
}

export async function rejectReview(
  uuid: string,
  payload: RejectReviewPayload
): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/review/reject`,
    { uuid, ...payload }
  );
  return response.data;
}

export async function requestChange(
  uuid: string,
  payload: RequestChangePayload
): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/request-change`,
    { uuid, ...payload }
  );
  return response.data;
}

export async function createChangeVersion(
  uuid: string,
  payload: CreateChangeVersionPayload
): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/change-version`,
    { uuid, ...payload }
  );
  return response.data;
}

export async function cancelChangeRequest(uuid: string): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/cancel-change`,
    { uuid }
  );
  return response.data;
}

export async function rejectChangeRequest(
  uuid: string,
  payload: RejectChangeRequestPayload
): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/reject-change`,
    { uuid, ...payload }
  );
  return response.data;
}

export async function approveChangeRequest(uuid: string): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/approve-change`,
    { uuid }
  );
  return response.data;
}

export async function deprecateTranslation(
  uuid: string,
  payload: DeprecateTranslationPayload
): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/deprecate`,
    { uuid, ...payload }
  );
  return response.data;
}

export async function rollbackTranslation(
  uuid: string,
  targetVersion: number
): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/rollback`,
    { uuid, targetVersion }
  );
  return response.data;
}

export async function resubmitCorrection(
  uuid: string,
  payload: ResubmitCorrectionPayload
): Promise<Translation> {
  const response = await apiClient.post<Translation>(
    `${BASE_PATH}/${uuid}/resubmit`,
    { uuid, ...payload }
  );
  return response.data;
}

// ---------- Bulk Operations ----------

export async function bulkSubmit(
  payload: BulkSubmitPayload
): Promise<BulkOperationResult> {
  const response = await apiClient.post<BulkOperationResult>(
    `${BASE_PATH}/bulk/submit`,
    payload
  );
  return response.data;
}

export async function bulkApprove(
  payload: BulkApprovePayload
): Promise<BulkOperationResult> {
  const response = await apiClient.post<BulkOperationResult>(
    `${BASE_PATH}/bulk/approve`,
    payload
  );
  return response.data;
}

export async function bulkReject(
  payload: BulkRejectPayload
): Promise<BulkOperationResult> {
  const response = await apiClient.post<BulkOperationResult>(
    `${BASE_PATH}/bulk/reject`,
    payload
  );
  return response.data;
}

export async function bulkDeprecate(
  payload: BulkDeprecatePayload
): Promise<BulkOperationResult> {
  const response = await apiClient.post<BulkOperationResult>(
    `${BASE_PATH}/bulk/deprecate`,
    payload
  );
  return response.data;
}

export async function bulkAssignTags(
  payload: BulkAssignTagsPayload
): Promise<BulkOperationResult> {
  const response = await apiClient.post<BulkOperationResult>(
    `${BASE_PATH}/bulk/tags`,
    payload
  );
  return response.data;
}

// ---------- Duplicate Detection ----------

export async function fetchDuplicateAnalysis(
  filters: DuplicateDetectionFilters = {}
): Promise<DuplicateAnalysis> {
  const params: Record<string, string | number> = {};
  if (filters.locale) params.locale = filters.locale;
  if (filters.domain) params.domain = filters.domain;
  if (filters.minSimilarity !== undefined)
    params.minSimilarity = filters.minSimilarity;

  const response = await apiClient.get<DuplicateAnalysis>(
    `${TOOLS_PATH}/duplicates`,
    params
  );
  return response.data;
}

export async function fetchSimilarByValue(
  filters: SearchSimilarByValueFilters
): Promise<SearchSimilarByValueResult> {
  const params: Record<string, string | number> = {
    value: filters.value,
    locale: filters.locale,
  };
  if (filters.domain) params.domain = filters.domain;
  if (filters.minSimilarity !== undefined)
    params.minSimilarity = filters.minSimilarity;
  if (filters.limit !== undefined) params.limit = filters.limit;

  const response = await apiClient.get<SearchSimilarByValueResult>(
    `${TOOLS_PATH}/duplicates/search-by-value`,
    params
  );
  return response.data;
}
